import { organization } from '@/lib/auth-client';
import type { Organization, Member, Invitation } from 'better-auth/plugins/organization';

/**
 * Organization Service
 *
 * Handles all organization-related business logic and API calls.
 * Provides a centralized interface for organization operations.
 */
export const organizationService = {
  /**
   * List all organizations for the current user
   */
  async list(): Promise<Organization[]> {
    const response = await organization.list({});
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to fetch organizations');
    }
    return response.data || [];
  },

  /**
   * Get the active organization member details
   */
  async getActiveMember() {
    const response = await organization.getActiveMember({});
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to fetch active organization');
    }
    return response.data;
  },

  /**
   * Set the active organization
   */
  async setActive(organizationId: string) {
    const response = await organization.setActive({
      organizationId,
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to switch organization');
    }
    return response.data;
  },

  /**
   * Create a new organization
   */
  async create(name: string, slug?: string) {
    const response = await organization.create({
      name: name.trim(),
      slug: slug || name.trim().toLowerCase().replace(/\s+/g, '-'),
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to create organization');
    }
    return response.data;
  },

  /**
   * List members of an organization
   */
  async listMembers(organizationId: string) {
    const response = await organization.listMembers({
      query: {
        organizationId,
      },
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to fetch members');
    }
    // API returns members in .data.members structure
    return response.data?.members || [];
  },

  /**
   * List invitations for an organization
   */
  async listInvitations(organizationId: string): Promise<Invitation[]> {
    const response = await organization.listInvitations({
      query: {
        organizationId,
      },
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to fetch invitations');
    }
    return response.data || [];
  },

  /**
   * Invite a member to an organization
   */
  async inviteMember(email: string, role: string, organizationId: string) {
    const response = await organization.inviteMember({
      email,
      role,
      organizationId,
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to send invitation');
    }
    return response.data;
  },

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitationId: string) {
    const response = await organization.cancelInvitation({
      invitationId,
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to cancel invitation');
    }
    return response.data;
  },

  /**
   * Remove a member from an organization
   */
  async removeMember(memberIdOrEmail: string) {
    const response = await organization.removeMember({
      memberIdOrEmail,
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to remove member');
    }
    return response.data;
  },

  /**
   * Get incoming invitations for the current user
   */
  async getIncomingInvitations() {
    const response = await fetch('/api/invitations/incoming', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch incoming invitations');
    }

    const data = await response.json();
    return data.invitations || [];
  },

  /**
   * Accept an invitation
   */
  async acceptInvitation(invitationId: string) {
    const response = await fetch(`/api/invitations/accept/${invitationId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to accept invitation');
    }

    const data = await response.json();
    return data;
  },

  /**
   * Decline an invitation
   */
  async declineInvitation(invitationId: string) {
    const response = await fetch(`/api/invitations/decline/${invitationId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to decline invitation');
    }

    const data = await response.json();
    return data;
  },
};